/*
 * useAlbumImage.ts
 * Custom hook that handles loading and scaling album images for the game board.
 * Converts base64 images to HTMLImageElement and scales them to match canvas dimensions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useDebug } from "@/utils/useDebugLog";

interface UseAlbumImageProps {
  imageSrc: string;
  canvasSize: number;
}

interface UseAlbumImageReturn {
  albumImage: HTMLImageElement | null;
  isLoading: boolean;
}

// Cache for loaded and scaled images - prevents re-fetching same images
const imageCache = new Map<string, HTMLImageElement>();

export function useAlbumImage({ imageSrc, canvasSize }: UseAlbumImageProps): UseAlbumImageReturn {
  const { log } = useDebug({ name: 'useAlbumImage', bgColor: 'goldenrod', enabled: true });
  const [albumImage, setAlbumImage] = useState<HTMLImageElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getAlbumImageFromServer = useCallback(async (): Promise<string> => {
    // log('Fetching album image:', imageSrc);
    const response = await fetch(imageSrc);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, [imageSrc]);

  useEffect(() => {
    const loadImage = async () => {
      // Create cache key from image source and canvas size
      const cacheKey = `${imageSrc}_${canvasSize}`;

      // Check if image is already cached
      const cachedImage = imageCache.get(cacheKey);
      if (cachedImage) {
        // log('Using cached image');
        setAlbumImage(cachedImage);
        setIsLoading(false);
        return;
      }

      // log('Starting image load process');
      setIsLoading(true);

      try {
        // Get base64 image from server
        const base64 = await getAlbumImageFromServer();

        const img = new Image();
        img.onload = () => {
          // Create a new canvas to scale the image to match canvas size
          const scaleCanvas = document.createElement('canvas');
          scaleCanvas.width = canvasSize;
          scaleCanvas.height = canvasSize;
          const scaleCtx = scaleCanvas.getContext('2d');

          if (scaleCtx) {
            // Calculate scaling to fit image into square canvas while maintaining aspect ratio
            const imgAspect = img.width / img.height;
            let drawWidth = canvasSize;
            let drawHeight = canvasSize;
            let offsetX = 0;
            let offsetY = 0;

            if (imgAspect > 1) {
              // Image is wider than tall
              drawHeight = canvasSize / imgAspect;
              offsetY = (canvasSize - drawHeight) / 2;
            } else {
              // Image is taller than wide
              drawWidth = canvasSize * imgAspect;
              offsetX = (canvasSize - drawWidth) / 2;
            }

            // Fill background with black
            scaleCtx.fillStyle = '#000';
            scaleCtx.fillRect(0, 0, canvasSize, canvasSize);

            // Draw scaled image centered
            scaleCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

            // Create new image from scaled canvas
            const scaledImg = new Image();
            scaledImg.onload = () => {
              // log('Image scaled and loaded successfully');
              imageCache.set(cacheKey, scaledImg);
              setAlbumImage(scaledImg);
              setIsLoading(false);
            };
            scaledImg.src = scaleCanvas.toDataURL();
          } else {
            // log('Failed to get 2D context for scaling');
            setIsLoading(false);
          }
        };
        img.onerror = () => {
          // log('Failed to load album image');
          setIsLoading(false);
        };
        img.src = base64;
      } catch (error) {
        // log('Error loading image:', error);
        setIsLoading(false);
      }
    };

    loadImage();

  }, [getAlbumImageFromServer, canvasSize]);

  return { albumImage, isLoading };
}
